import React from 'react';
import Lightbox from 'components/shared/Lightbox';
import { getMimeLabel, formatFileSize, isImageMime } from '../utils';

const EMPTY = [];

const buildMeta = (file) => {
  const parts = [];
  if (file.mime_type) parts.push(getMimeLabel(file.mime_type));
  if (file.size_bytes) parts.push(formatFileSize(file.size_bytes));
  return parts.join(' • ');
};

const useFileLightbox = ({ files, onView, onDownload, subtitle }) => {
  const list = files || EMPTY;
  const imageFiles = React.useMemo(() => list.filter((f) => isImageMime(f.mime_type)), [list]);
  const [lightboxIndex, setLightboxIndex] = React.useState(null);

  const handleView = React.useCallback(
    (payload) => {
      const file = list.find((f) => f.id === payload.id);
      if (file && isImageMime(file.mime_type)) {
        const idx = imageFiles.findIndex((f) => f.id === file.id);
        if (idx >= 0) {
          setLightboxIndex(idx);
          return;
        }
      }
      onView(payload);
    },
    [list, imageFiles, onView],
  );

  const handleClose = React.useCallback(() => setLightboxIndex(null), []);

  const lightboxImages = React.useMemo(
    () =>
      imageFiles.map((f) => ({
        id: f.id,
        url: f.url,
        title: f.display_name || f.filename,
        meta: buildMeta(f),
      })),
    [imageFiles],
  );

  const handleDownload = React.useCallback(
    (image) => {
      if (!onDownload) return;
      const file = imageFiles.find((f) => f.id === image.id);
      if (!file) return;
      onDownload({ id: file.id, filename: file.display_name || file.filename });
    },
    [onDownload, imageFiles],
  );

  const lightboxElement =
    lightboxIndex !== null ? (
      <Lightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        onClose={handleClose}
        onDownload={onDownload ? handleDownload : undefined}
        variant="light"
        subtitle={subtitle}
        readOnly
      />
    ) : null;

  return { handleView, lightboxElement };
};

export default useFileLightbox;
